import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight, Newspaper, RefreshCw } from 'lucide-react';

import NewsBreakingBar from './NewsBreakingBar';
import NewsCard from './NewsCard';
import { fetchNews, selectNewsArticles } from '../../store/slices/newsSlice';
import { NEWS_FILTERS } from '../../utils/newsMapper';

const FEATURED_COUNT = 4;

const formatRefreshedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden animate-pulse">
    <div className="h-24 sm:h-28 bg-slate-200" />
    <div className="p-4 sm:p-5 space-y-2.5">
      <div className="w-24 h-3 bg-slate-100 rounded" />
      <div className="w-full h-4 bg-slate-200 rounded" />
      <div className="w-3/4 h-4 bg-slate-200 rounded" />
      <div className="w-full h-3 bg-slate-100 rounded mt-3" />
      <div className="w-5/6 h-3 bg-slate-100 rounded" />
    </div>
  </div>
);

export const NewsSection = () => {
  const dispatch = useDispatch();
  const articles = useSelector(selectNewsArticles);
  const { refreshedAt, source, status, error } = useSelector((state) => state.news);

  const [activeFilter, setActiveFilter] = useState('all');

  // Keep showing loaded articles while a refresh is in flight or after it fails
  const hasArticles = articles.length > 0;
  const isLoading = !hasArticles && (status === 'idle' || status === 'loading');
  const isFailed = !hasArticles && status === 'failed';

  useEffect(() => {
    dispatch(fetchNews({ maxAgeMs: 5 * 60000 }));
  }, [dispatch]);

  const filteredArticles = useMemo(
    () => articles.filter((article) => article.filters.includes(activeFilter)),
    [articles, activeFilter]
  );
  const visibleArticles = filteredArticles.slice(0, FEATURED_COUNT);
  const activeLabel = NEWS_FILTERS.find((filter) => filter.key === activeFilter)?.label;

  const handleRetry = () => dispatch(fetchNews());

  const handleFilter = (key) => {
    setActiveFilter(key);
  };

  const refreshedLabel = formatRefreshedAt(refreshedAt);

  let content;
  if (isLoading) {
    content = Array.from({ length: FEATURED_COUNT }).map((_, idx) => <SkeletonCard key={idx} />);
  } else if (isFailed) {
    content = (
      <div className="col-span-full py-12 text-center">
        <div className="max-w-md mx-auto space-y-3 px-4">
          <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
            <RefreshCw className="w-5 h-5" />
          </div>
          <p className="text-slate-700 font-semibold text-sm">News is temporarily unavailable</p>
          <p className="text-slate-400 text-xs">{error}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retry Loading News
          </button>
        </div>
      </div>
    );
  } else if (visibleArticles.length === 0) {
    content = (
      <div className="col-span-full py-12 text-center">
        <div className="max-w-md mx-auto space-y-2 text-slate-500 px-4">
          <Newspaper className="w-8 h-8 mx-auto text-slate-300" />
          <p className="font-semibold text-slate-700 text-sm">
            {hasArticles ? `No ${activeLabel} stories in the latest news` : 'No IPO news right now'}
          </p>
          {hasArticles && (
            <button
              type="button"
              onClick={() => handleFilter('all')}
              className="mt-2 text-xs text-indigo-600 font-semibold hover:underline"
            >
              Show All News
            </button>
          )}
        </div>
      </div>
    );
  } else {
    content = visibleArticles.map((article) => <NewsCard key={article.id} article={article} />);
  }

  return (
    <section id="ipo-news" className="py-12 sm:py-16 bg-white border-t border-slate-100">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 sm:gap-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-600" />
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                IPO News & Market Updates
              </h2>
            </div>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              Stay ahead with real-time reporting, SEBI approvals, issue sizes, and analyst insights
            </p>
            {refreshedLabel && (
              <p className="text-[11px] text-slate-400 mt-1">
                Updated {refreshedLabel}
                {source ? ` · via ${source}` : ''}
              </p>
            )}
          </div>

          {/* Topic Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0 scrollbar-none">
            {NEWS_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={() => handleFilter(filter.key)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  activeFilter === filter.key
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                {filter.label}
              </button>
            ))}

            <Link
              to="/ipo-news"
              className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-700 pl-2 whitespace-nowrap"
            >
              <span>View All News</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Breaking News Marquee */}
        <NewsBreakingBar articles={articles} isLoading={isLoading} />

        {/* Featured News Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mt-4">{content}</div>

      </div>
    </section>
  );
};

export default NewsSection;
